import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { GetAccountsResponse } from '@iwoca/lapi-client/edge';
import { Modal } from '@iwoca/orion';

import ApprovalStatusLines from './ApprovalStatusLines';
import styles from './OutstandingLoanView.module.css';
import { EarlyRepaymentSVG } from '../../../../svgComponents/earlyRepaymentSVG';
import { getInterestRateFromCBILSAccount } from '../../../multiLoanDashboard/accountsSelector';
import { ActionButtonMakeRepayment } from '../../../multiLoanDashboard/dashboardComponents/actionButtonMakeRepayment';
import InternationalisedName from '../../../multiLoanDashboard/dashboardComponents/InternationalisedName';
import HelpModal from '../../../navigation/helpModal/HelpModal';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import { TitleSectionWrapper } from '../../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { SpillBanner } from '../../../spill/SpillBanner';
import { useShowSpillBanner } from '../../../spill/useShowSpillBanner';
import { ActionButton } from '../../../ui/actionButton';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/index';
import { Rate } from '../../../ui/rate/Rate';

type Account = NonNullable<GetAccountsResponse['data']>['accounts'][number];

export const OutstandingLoanView = ({ account }: { account: Account }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isCustomerOverdue = account.loan_status === 'overdue';

    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: account.account_id!,
            product_type: 'cbils',
            dashboard_state: isCustomerOverdue ? 'overdue' : 'ongoing'
        });
    }, [account, isCustomerOverdue]);

    const handleOpenModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const outstandingBalance = account.balance!.total;
    const approvalStatusPrincipalBalance = account.balance!.principal;
    const interestRate = getInterestRateFromCBILSAccount(account);
    const isEarlyRepaymentEnabled =
        account.repayment_details?.stripe_publishable_key || false;

    const { showSpillBanner, closeSpillBanner } = useShowSpillBanner();

    return (
        <>
            {showSpillBanner && (
                <div className={styles.bannerContainer}>
                    <SpillBanner onClose={closeSpillBanner} legacy />
                </div>
            )}
            <div data-testid="outstanding-loan-view">
                <TitleSectionWrapper withBackground={true}>
                    <div data-testid="headlineText" className={styles.headline}>
                        Hi <InternationalisedName />, your outstanding balance
                        is{' '}
                        <MoneyWithDecimals
                            value={outstandingBalance}
                            decimalWithSeparatorClassName={
                                styles.decimalsWithSeparator
                            }
                        />
                    </div>

                    <ApprovalStatusLines isPaymentOverdue={isCustomerOverdue} />

                    <div data-testid="credit-outstanding">
                        <div className={styles.description}>
                            Outstanding principal:
                        </div>
                        <span className={styles.amount}>
                            <MoneyWithDecimals
                                decimalWithSeparatorClassName={
                                    styles.decimalsWithSeparator
                                }
                                value={approvalStatusPrincipalBalance}
                            />
                        </span>
                        <div>
                            <Rate value={interestRate!} decimalPlaces={2} />{' '}
                            Interest rate per 30 days (interest covered by the
                            government for the first 12 months)
                        </div>
                    </div>
                </TitleSectionWrapper>
                <MainSectionWrapper>
                    {isEarlyRepaymentEnabled ? (
                        <ActionButtonMakeRepayment
                            outstandingBalance={outstandingBalance!}
                            accountId={account.account_id!}
                        />
                    ) : (
                        <ActionButton
                            label="To make an early repayment give us a call"
                            actionIcon={EarlyRepaymentSVG}
                            disabled={false}
                            showIcon={true}
                            onClick={handleOpenModal}
                        />
                    )}
                </MainSectionWrapper>
            </div>
            <Modal isOpen={isModalOpen} onClose={handleOpenModal}>
                <HelpModal />
            </Modal>
        </>
    );
};
